import { useState } from "react";
import "@/App.css";
import { 
  ShieldAlert, 
  MapPin,
  Phone,
  Clock,
  DollarSign,
  CheckCircle,
  Users,
  Star,
  Briefcase,
  ChevronRight,
  Globe,
  AlertTriangle
} from "lucide-react";
import { Link } from "react-router-dom";

function Jobs() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-sm border-b border-white/10">
        <div className="section-container flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3">
            <ShieldAlert className="w-8 h-8 text-orange-500" strokeWidth={1.5} />
            <span 
              className="font-bold text-xl tracking-tight uppercase"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="logo-text"
            >
              Simon Advanced Security
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="footer-link hover:text-orange-500" data-testid="nav-home">
              Home
            </Link>
            <a 
              href="tel:+18008241906" 
              className="btn-primary text-sm py-2 px-4"
              data-testid="nav-call-btn"
            >
              Call Now
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-[#0A0A0A] relative overflow-hidden" data-testid="jobs-hero">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 50px, rgba(249,115,22,0.1) 50px, rgba(249,115,22,0.1) 51px),
                              repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(249,115,22,0.1) 50px, rgba(249,115,22,0.1) 51px)`
          }} />
        </div>
        
        <div className="section-container relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <p 
              className="text-orange-500 text-xs md:text-sm tracking-[0.3em] uppercase mb-4"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              Career Opportunities
            </p>
            <h1 
              className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter uppercase text-white mb-6"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="jobs-headline"
            >
              Join Our Security Team
            </h1>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
              Are you tired of companies that base your pay on how many certificates you have or how long you've been in the industry? We believe in rewarding what matters most: your work ethic and dedication to excellence.
            </p>
          </div>
        </div>
      </section>

      {/* International Positions */}
      <section className="py-12 bg-[#121212]" data-testid="international-jobs-section">
        <div className="section-container">
          <p 
            className="text-orange-500 text-xs tracking-[0.3em] uppercase mb-6 text-center"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            International Positions
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Venezuela - Country Director */}
            <Link 
              to="/jobs/country-director-venezuela"
              className="block bg-black/40 border border-white/10 p-6 hover:border-orange-500/50 transition-all group"
              data-testid="job-card-venezuela"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-red-500/20 border border-red-500/50 text-red-400 text-[10px] px-2 py-1 uppercase tracking-wider">
                  High Threat
                </span>
                <span className="bg-orange-500/20 border border-orange-500/50 text-orange-400 text-[10px] px-2 py-1 uppercase tracking-wider">
                  Executive
                </span>
              </div>
              <h3 
                className="text-xl font-bold uppercase tracking-tight text-white mb-2 group-hover:text-orange-500 transition-colors"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Country Director – Venezuela
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                Operational command of all security operations across multiple concurrent client programs.
              </p>
              <div className="flex flex-wrap justify-between items-end">
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <Globe className="w-4 h-4" strokeWidth={1.5} />
                  <span>Venezuela</span>
                </div>
                <p className="text-orange-500 text-xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  $410,000/yr
                </p>
              </div>
            </Link>

            {/* Ukraine - Extraction */}
            <Link 
              to="/jobs/extraction-agents-ukraine"
              className="block bg-black/40 border border-white/10 p-6 hover:border-orange-500/50 transition-all group"
              data-testid="job-card-ukraine"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-red-500/20 border border-red-500/50 text-red-400 text-[10px] px-2 py-1 uppercase tracking-wider">
                  Active Warzone
                </span>
                <span className="bg-orange-500/20 border border-orange-500/50 text-orange-400 text-[10px] px-2 py-1 uppercase tracking-wider">
                  Extraction
                </span>
              </div>
              <h3 
                className="text-xl font-bold uppercase tracking-tight text-white mb-2 group-hover:text-orange-500 transition-colors"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Extraction / Protective Agents – Ukraine
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                Covert extraction operations of individuals and families throughout Ukraine.
              </p>
              <div className="flex flex-wrap justify-between items-end">
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <Globe className="w-4 h-4" strokeWidth={1.5} />
                  <span>Ukraine</span>
                </div>
                <p className="text-orange-500 text-xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  $800-$1,500/day
                </p>
              </div>
            </Link>

            {/* Guatemala - EP Team */}
            <Link 
              to="/jobs/executive-protection-guatemala"
              className="block bg-black/40 border border-white/10 p-6 hover:border-orange-500/50 transition-all group"
              data-testid="job-card-guatemala"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-orange-500/20 border border-orange-500/50 text-orange-400 text-[10px] px-2 py-1 uppercase tracking-wider">
                  Covert Ops
                </span>
                <span className="bg-blue-500/20 border border-blue-500/50 text-blue-400 text-[10px] px-2 py-1 uppercase tracking-wider">
                  U.S. Only
                </span>
              </div>
              <h3 
                className="text-xl font-bold uppercase tracking-tight text-white mb-2 group-hover:text-orange-500 transition-colors"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Executive Protection Team – Guatemala
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                30-day covert protection assignment supporting a U.S. principal.
              </p>
              <div className="flex flex-wrap justify-between items-end">
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <Globe className="w-4 h-4" strokeWidth={1.5} />
                  <span>Guatemala</span>
                </div>
                <p className="text-orange-500 text-xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  $600/day
                </p>
              </div>
            </Link>

            {/* Philippines - EP Detail */}
            <Link 
              to="/jobs/executive-protection-philippines"
              className="block bg-black/40 border border-white/10 p-6 hover:border-orange-500/50 transition-all group"
              data-testid="job-card-philippines"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-green-500/20 border border-green-500/50 text-green-400 text-[10px] px-2 py-1 uppercase tracking-wider">
                  Long-Term
                </span>
                <span className="bg-orange-500/20 border border-orange-500/50 text-orange-400 text-[10px] px-2 py-1 uppercase tracking-wider">
                  Executive Protection
                </span>
              </div>
              <h3 
                className="text-xl font-bold uppercase tracking-tight text-white mb-2 group-hover:text-orange-500 transition-colors"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Executive Protection Detail – Manila, Philippines
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                Large-scale EP details for long-term contract employment.
              </p>
              <div className="flex flex-wrap justify-between items-end">
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <Globe className="w-4 h-4" strokeWidth={1.5} />
                  <span>Philippines</span>
                </div>
                <p className="text-orange-500 text-xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  $400-$700/day
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Starting Pay Highlight */}
      <section className="py-16 bg-[#0A0A0A]" data-testid="pay-section">
        <div className="section-container">
          <div className="bg-orange-500/10 border border-orange-500/30 p-8 md:p-12 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-orange-500" />
            <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-orange-500" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-orange-500" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-orange-500" />
            
            <p 
              className="text-orange-500 text-xs tracking-[0.3em] uppercase mb-4"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              Now Hiring Event Security Professionals
            </p>
            <div className="flex items-center justify-center gap-4 mb-4">
              <DollarSign className="w-12 h-12 text-orange-500" strokeWidth={1.5} />
              <h2 
                className="text-5xl md:text-7xl font-black tracking-tighter uppercase text-white"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Starting Pay: $18/HR
              </h2>
            </div>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
              We believe great security professionals deserve fair compensation from day one. 
              Your pay increases are tied to your work ethic and performance — not your resume.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-[#121212]" data-testid="why-section">
        <div className="section-container">
          <div className="text-center mb-16">
            <p 
              className="text-orange-500 text-xs tracking-[0.3em] uppercase mb-4"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              Benefits
            </p>
            <h2 
              className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Why Choose Us?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="service-card" data-testid="benefit-1">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-orange-500/10 flex-shrink-0 flex items-center justify-center">
                  <Star className="w-8 h-8 text-orange-500" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 
                    className="text-xl font-bold uppercase tracking-tight text-white mb-3"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    Performance-Based Advancement
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Your pay increases are tied to your work ethic, reliability, and professional 
                    conduct — not how many licenses you hold or how long you've had them.
                  </p>
                </div>
              </div>
            </div>

            <div className="service-card" data-testid="benefit-2">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-orange-500/10 flex-shrink-0 flex items-center justify-center">
                  <Briefcase className="w-8 h-8 text-orange-500" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 
                    className="text-xl font-bold uppercase tracking-tight text-white mb-3"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    Immediate Openings
                  </h3>
                  <p className="text-gray-400 text-sm">
                    We have positions available now in Orlando, Florida and throughout New Jersey. 
                    Start your new career path today.
                  </p>
                </div>
              </div>
            </div>

            <div className="service-card" data-testid="benefit-3">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-orange-500/10 flex-shrink-0 flex items-center justify-center">
                  <Clock className="w-8 h-8 text-orange-500" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 
                    className="text-xl font-bold uppercase tracking-tight text-white mb-3"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    Flexible Scheduling
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Full-time and part-time positions available. We work with your schedule 
                    to find the right fit for your lifestyle.
                  </p>
                </div>
              </div>
            </div>

            <div className="service-card" data-testid="benefit-4">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-orange-500/10 flex-shrink-0 flex items-center justify-center">
                  <Users className="w-8 h-8 text-orange-500" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 
                    className="text-xl font-bold uppercase tracking-tight text-white mb-3"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    Supportive Team Environment
                  </h3>
                  <p className="text-gray-400 text-sm">
                    We invest in our people because you're our greatest asset. Join a team 
                    that values and supports your growth.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-24 bg-[#121212]" data-testid="requirements-section">
        <div className="section-container">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Requirements Column */}
            <div>
              <p 
                className="text-orange-500 text-xs tracking-[0.3em] uppercase mb-4"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                Qualifications
              </p>
              <h2 
                className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-8"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Requirements
              </h2>
              <ul className="space-y-4">
                {[
                  "Must be able to obtain proper security licensing in your state",
                  "Clean background check",
                  "Reliable transportation",
                  "Strong communication skills",
                  "Professional appearance",
                  "Ability to stand for extended periods"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span className="text-gray-300 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* What We're Looking For Column */}
            <div>
              <p 
                className="text-orange-500 text-xs tracking-[0.3em] uppercase mb-4"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                Ideal Candidates
              </p>
              <h2 
                className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-8"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                What We're Looking For
              </h2>
              <p className="text-gray-400 text-sm mb-6">
                We're not concerned with how many certificates you have or how long you've been 
                in the industry. We're looking for individuals who:
              </p>
              <ul className="space-y-4">
                {[
                  "Show up on time, every time",
                  "Take initiative",
                  "Communicate effectively",
                  "Maintain professionalism in all situations",
                  "Have a genuine interest in keeping people and property safe"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span className="text-gray-300 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Apply CTA */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="apply-section">
        <div className="section-container">
          <div className="text-center max-w-3xl mx-auto">
            <p 
              className="text-orange-500 text-xs tracking-[0.3em] uppercase mb-4"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              Start Your Career
            </p>
            <h2 
              className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Apply Today
            </h2>
            <p className="text-gray-400 text-base md:text-lg mb-8">
              Positions are filling quickly in both Orlando and New Jersey locations. 
              Don't miss this opportunity to join a security team that values your 
              work ethic over your paperwork.
            </p>
            
            <div className="bg-black/40 border border-white/10 p-8 mb-8">
              <p className="text-gray-300 text-lg mb-6">
                To apply, call to schedule an interview:
              </p>
              <a 
                href="tel:+18008241906"
                className="inline-flex items-center gap-3 text-3xl md:text-4xl font-bold text-orange-500 hover:text-orange-400 transition-colors"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                data-testid="apply-phone"
              >
                <Phone className="w-8 h-8" strokeWidth={1.5} />
                800-824-1906
              </a>
            </div>

            <p 
              className="text-gray-500 text-sm tracking-widest uppercase"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              Simon Advanced Security — Where Performance Matters More Than Paperwork
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#0A0A0A] border-t border-white/10">
        <div className="section-container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-3">
              <ShieldAlert className="w-6 h-6 text-orange-500" strokeWidth={1.5} />
              <span 
                className="font-bold text-lg tracking-tight uppercase text-white"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Simon Advanced Security
              </span>
            </Link>
            <p className="text-gray-500 text-xs tracking-widest">
              © {new Date().getFullYear()} ALL RIGHTS RESERVED
            </p>
            <div className="flex items-center gap-6">
              <Link to="/" className="footer-link" data-testid="footer-home">Home</Link>
              <a href="tel:+18008241906" className="footer-link" data-testid="footer-call">Call Us</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Jobs;
